import { useState, useRef } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useSnackbar } from 'notistack'
import { AiExtractedProduct } from '../SuperAdminAiProducts/useAiProductsApi'
import { AiProductPreview } from '../SuperAdminAiProducts/AiProductPreview'
import { useProductApi } from '../../organizer/events/api/useProductApi'
import { useCompanyContext } from '../../../contexts/company-context'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

const JSON_SCHEMA_EXAMPLE = `{
  "name": "Kolonie letnie nad morzem - Łeba 2025",
  "type": "kolonie",
  "description": "Opis oferty...",
  "categories": ["wypoczynek"],
  "min_age": 7,
  "max_age": 16,
  "duration_days": 14,
  "trip_type": "stacjonarne",
  "country_code": "PL",
  "country_name": "Polska",
  "location": {
    "province": "pomorskie",
    "city": "Łeba",
    "address": "ul. Nadmorska 10"
  },
  "location_types": ["morze"],
  "location_name": "Łeba",
  "transport_type": "zorganizowany",
  "transport_description": "Autokar klimatyzowany z Warszawy i Łodzi",
  "food_type": "pelne-wyzywienie",
  "food_description": "5 posiłków dziennie, kuchnia domowa",
  "insurance_type": "w-cenie",
  "insurance_description": "NNW + KL",
  "schedule_description": "Plan dnia: ...",
  "schedule": [
    { "day": 1, "title": "Przyjazd", "description": "Zakwaterowanie, zapoznanie" }
  ],
  "participation_conditions": "Karta kwalifikacyjna uczestnika",
  "min_price": 289900,
  "price_description": "Cena za osobę za 14 dni",
  "price_includes_text": "Zakwaterowanie, wyżywienie, transport, ubezpieczenie",
  "price_not_includes_text": "Kieszonkowe",
  "discounts_description": "Rodzeństwo -5%",
  "tags": ["morze", "sport", "przygoda"],
  "trips": [
    {
      "name": "Turnus I",
      "start_date": "2025-07-01",
      "end_date": "2025-07-14",
      "full_price": 289900,
      "advance_price": 50000,
      "available_spots": 40
    }
  ]
}`

const PROMPT_INSTRUCTIONS = `## Jak wygenerować JSON produktu za pomocą AI (ChatGPT / Claude / Gemini)

Użyj poniższego promptu, wklejając opis oferty turystycznej:

\`\`\`
Na podstawie poniższego opisu oferty turystycznej, wygeneruj obiekt JSON w podanym formacie.

ZASADY:
- Typ produktu ("type") to jeden z: "kolonie", "polkolonie", "wycieczka-szkolna", "zielona-szkola", "noclegi-dla-grup"
- Ceny (min_price, full_price, advance_price) podaj w GROSZACH (np. 2899 zł = 289900)
- Daty w formacie ISO: "YYYY-MM-DD"
- location_types: "morze", "góry", "jezioro", "las", "miasto"
- food_type: "3-posilki", "pelne-wyzywienie", "sniadanie", "bez-wyzywienia", "wlasne"
- transport_type: "wlasny", "zorganizowany", "mieszany"
- insurance_type: "w-cenie", "dodatkowe", "brak"
- categories: "wypoczynek", "sport", "edukacja", "przygoda", "jezyki", "taniec", "sztuka", "programowanie", "inne"

FORMAT OUTPUT (JSON):
{
  "name": "string - pełna nazwa oferty",
  "type": "string - typ produktu",
  "description": "string - szczegółowy opis HTML lub plain text",
  "categories": ["string"],
  "min_age": number,
  "max_age": number,
  "duration_days": number,
  "trip_type": "string - np. stacjonarne, objazdowe",
  "country_code": "string - np. PL, IT, ES",
  "country_name": "string - np. Polska",
  "location": {
    "province": "string",
    "city": "string",
    "address": "string",
    "driving_directions": "string (opcjonalnie)"
  },
  "location_types": ["string"],
  "location_name": "string",
  "transport_type": "string",
  "transport_description": "string",
  "food_type": "string",
  "food_description": "string",
  "food_vegetarian": boolean,
  "food_vegan": boolean,
  "insurance_type": "string",
  "insurance_description": "string",
  "schedule_description": "string",
  "schedule": [{ "day": number, "title": "string", "description": "string" }],
  "participation_conditions": "string",
  "conditions_text_short": "string",
  "conditions_text_long": "string",
  "additional_info_text": "string",
  "min_price": number (w groszach),
  "price_description": "string",
  "price_includes_text": "string",
  "price_not_includes_text": "string",
  "discounts_description": "string",
  "tags": ["string"],
  "trips": [
    {
      "name": "string - nazwa turnusu",
      "start_date": "YYYY-MM-DD",
      "end_date": "YYYY-MM-DD",
      "full_price": number (w groszach),
      "advance_price": number (w groszach),
      "available_spots": number
    }
  ]
}

Zwróć TYLKO obiekt JSON, bez dodatkowego tekstu.

OPIS OFERTY:
[tutaj wklej opis]
\`\`\`
`

export const JsonImportProductPage = () => {
    const [jsonInput, setJsonInput] = useState('')
    const [parsedData, setParsedData] = useState<AiExtractedProduct | null>(null)
    const [editedData, setEditedData] = useState<AiExtractedProduct | null>(null)
    const [parseError, setParseError] = useState<string | null>(null)
    const [isCreating, setIsCreating] = useState(false)
    const [showPromptInfo, setShowPromptInfo] = useState(false)

    const fileInputRef = useRef<HTMLInputElement>(null)

    const { createProduct, createTrip } = useProductApi()
    const companyContext = useCompanyContext()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const validateAndParseJson = (text: string): AiExtractedProduct | null => {
        try {
            const data = JSON.parse(text)

            // Basic validation
            if (!data.name || typeof data.name !== 'string') {
                setParseError('Brak wymaganego pola "name" (nazwa produktu)')
                return null
            }
            if (!data.type || !['kolonie', 'polkolonie', 'wycieczka-szkolna', 'zielona-szkola', 'noclegi-dla-grup'].includes(data.type)) {
                setParseError('Pole "type" musi być jednym z: kolonie, polkolonie, wycieczka-szkolna, zielona-szkola, noclegi-dla-grup')
                return null
            }
            if (!data.description || typeof data.description !== 'string') {
                setParseError('Brak wymaganego pola "description" (opis produktu)')
                return null
            }

            setParseError(null)
            return data as AiExtractedProduct
        } catch (e: any) {
            setParseError(`Błąd parsowania JSON: ${e.message}`)
            return null
        }
    }

    const handleParseJson = () => {
        if (jsonInput.trim().length < 10) {
            setParseError('Wklej prawidłowy obiekt JSON')
            return
        }
        const data = validateAndParseJson(jsonInput)
        if (data) {
            setParsedData(data)
            setEditedData(data)
        }
    }

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        if (!file.name.endsWith('.json')) {
            setParseError('Akceptowane są tylko pliki .json')
            return
        }

        if (file.size > 1024 * 1024) {
            setParseError('Plik jest za duży (maks. 1 MB)')
            return
        }

        const reader = new FileReader()
        reader.onload = (e) => {
            const text = e.target?.result as string
            setJsonInput(text)
            const data = validateAndParseJson(text)
            if (data) {
                setParsedData(data)
                setEditedData(data)
            }
        }
        reader.onerror = () => {
            setParseError('Błąd odczytu pliku')
        }
        reader.readAsText(file)

        // Reset input so the same file can be uploaded again
        event.target.value = ''
    }

    const handleCreate = async () => {
        if (!editedData) return

        if (!companyContext.company?.id) {
            enqueueSnackbar('Wybierz firmę przed utworzeniem produktu.', {
                variant: 'warning'
            })
            return
        }

        setIsCreating(true)
        try {
            const productId = uuidv4()

            const category = editedData.categories?.[0]
                ? { id: editedData.categories[0], code: editedData.categories[0] }
                : { id: 'inne', code: 'inne' }

            const productPayload: any = {
                id: productId,
                name: editedData.name,
                type: editedData.type,
                description: editedData.description,
                company_id: companyContext.company.id,
                category: category,
                categories: editedData.categories,
                min_age: editedData.min_age,
                max_age: editedData.max_age,
                duration_days: editedData.duration_days,
                trip_type: editedData.trip_type,
                country_code: editedData.country_code ?? 'PL',
                country_name: editedData.country_name ?? 'Polska',
                location: editedData.location,
                location_types: editedData.location_types,
                location_name: editedData.location_name,
                transport_type: editedData.transport_type,
                transport_description: editedData.transport_description,
                food_type: editedData.food_type,
                food_description: editedData.food_description,
                food_vegetarian: editedData.food_vegetarian,
                food_vegan: editedData.food_vegan,
                insurance_type: editedData.insurance_type,
                insurance_description: editedData.insurance_description,
                schedule_description: editedData.schedule_description,
                schedule: editedData.schedule,
                participation_conditions: editedData.participation_conditions,
                conditions_text_short: editedData.conditions_text_short,
                conditions_text_long: editedData.conditions_text_long,
                additional_info_text: editedData.additional_info_text,
                min_price: editedData.min_price,
                price_description: editedData.price_description,
                price_includes_text: editedData.price_includes_text,
                price_not_includes_text: editedData.price_not_includes_text,
                discounts_description: editedData.discounts_description,
                tags: editedData.tags,
                availability: editedData.availability
            }

            // Remove undefined values
            Object.keys(productPayload).forEach(key => {
                if (productPayload[key] === undefined) {
                    delete productPayload[key]
                }
            })

            const result = await createProduct(productPayload)
            const createdProductId = result?.product?.id ?? productId

            // Create trips if present
            if (editedData.trips && editedData.trips.length > 0) {
                for (const trip of editedData.trips) {
                    try {
                        const tripId = uuidv4()
                        await createTrip(createdProductId, {
                            id: tripId,
                            product_id: createdProductId,
                            name: trip.name ?? editedData.name,
                            start_date: trip.start_date,
                            end_date: trip.end_date,
                            full_price: trip.full_price,
                            advance_price: trip.advance_price,
                            available_spots: trip.available_spots,
                            company_id: companyContext.company.id,
                            product_type: editedData.type,
                            currency: 'PLN'
                        })
                    } catch (tripErr) {
                        console.error('Błąd tworzenia terminu:', tripErr)
                    }
                }
            }

            enqueueSnackbar(
                `Produkt "${editedData.name}" został utworzony${editedData.trips?.length
                    ? ` wraz z ${editedData.trips.length} terminami`
                    : ''
                }.`,
                { variant: 'success' }
            )

            const typeRoute = mapProductTypeToRoute(editedData.type)
            navigate(`/products/${typeRoute}/${createdProductId}/details`)
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'Wystąpił błąd podczas tworzenia produktu.'
            enqueueSnackbar(message, { variant: 'error' })
        } finally {
            setIsCreating(false)
        }
    }

    const handleReset = () => {
        setParsedData(null)
        setEditedData(null)
        setParseError(null)
        setJsonInput('')
    }

    return (
        <div className="max-w-[1200px]">
            <div className="flex items-center gap-3 mb-6">
                <UploadFileIcon sx={{ fontSize: 32, color: '#6366f1' }} />
                <h1 className="text-[28px] font-bold">
                    Import produktu z JSON
                </h1>
            </div>

            {/* Prompt info banner - SuperAdmin only */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setShowPromptInfo(!showPromptInfo)}
                >
                    <InfoOutlinedIcon sx={{ color: '#6366f1' }} />
                    <span className="font-semibold text-indigo-700">
                        Jak przygotować JSON produktu za pomocą AI?
                    </span>
                    <span className="text-indigo-500 text-sm ml-auto">
                        {showPromptInfo ? 'Zwiń ▲' : 'Rozwiń ▼'}
                    </span>
                </div>

                {showPromptInfo && (
                    <div className="mt-4 prose prose-sm max-w-none">
                        <div className="bg-white rounded p-4 border border-indigo-100 whitespace-pre-wrap text-sm font-mono overflow-x-auto">
                            {PROMPT_INSTRUCTIONS}
                        </div>
                        <div className="mt-4">
                            <p className="text-sm text-gray-600 font-sans">
                                <strong>Przykładowy JSON:</strong>
                            </p>
                            <pre className="bg-gray-900 text-green-300 p-4 rounded text-xs overflow-x-auto">
                                {JSON_SCHEMA_EXAMPLE}
                            </pre>
                        </div>
                    </div>
                )}
            </div>

            {/* Step 1: JSON input */}
            {!parsedData && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Wklej JSON lub załaduj plik
                    </h2>

                    <TextField
                        multiline
                        minRows={12}
                        maxRows={30}
                        fullWidth
                        value={jsonInput}
                        onChange={e => setJsonInput(e.target.value)}
                        placeholder='Wklej tutaj obiekt JSON produktu...'
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: 'monospace',
                                fontSize: '13px'
                            }
                        }}
                    />

                    <div className="flex items-center gap-4 mt-4">
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleParseJson}
                            disabled={jsonInput.trim().length < 10}
                            startIcon={<ContentPasteIcon />}
                            sx={{
                                backgroundColor: '#6366f1',
                                '&:hover': { backgroundColor: '#4f46e5' }
                            }}
                        >
                            Parsuj JSON
                        </Button>

                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => fileInputRef.current?.click()}
                            startIcon={<UploadFileIcon />}
                        >
                            Załaduj plik .json
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            className="hidden"
                            onChange={handleFileUpload}
                        />

                        {jsonInput.length > 0 && (
                            <span className="text-sm text-gray-400">
                                {jsonInput.length} znaków
                            </span>
                        )}
                    </div>

                    {parseError && (
                        <Alert severity="error" className="mt-4">
                            {parseError}
                        </Alert>
                    )}
                </div>
            )}

            {/* Step 2: Preview & edit parsed data */}
            {parsedData && editedData && (
                <div>
                    <Alert severity="success" className="mb-4">
                        JSON został sparsowany pomyślnie. Sprawdź dane i utwórz produkt.
                    </Alert>

                    <AiProductPreview
                        data={editedData}
                        onChange={setEditedData}
                    />

                    <div className="flex gap-4 mt-6 mb-8 sticky bottom-4 bg-white p-4 rounded-lg shadow-lg border">
                        <Button
                            variant="contained"
                            size="large"
                            color="success"
                            onClick={handleCreate}
                            disabled={isCreating || !companyContext.company?.id}
                            startIcon={
                                isCreating ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : null
                            }
                        >
                            {isCreating ? 'Tworzę produkt...' : 'Utwórz produkt'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={handleReset}
                            disabled={isCreating}
                        >
                            Importuj inny JSON
                        </Button>
                        {!companyContext.company?.id && (
                            <Alert severity="warning" sx={{ py: 0 }}>
                                Wybierz firmę w nagłówku, aby utworzyć produkt.
                            </Alert>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function mapProductTypeToRoute(type: string): string {
    switch (type) {
        case 'kolonie':
            return 'camp'
        case 'polkolonie':
            return 'daycamp'
        case 'wycieczka-szkolna':
        case 'zielona-szkola':
            return 'schooltrip'
        case 'noclegi-dla-grup':
            return 'groups'
        default:
            return 'camp'
    }
}
